import { useHeaderHeight } from '@react-navigation/elements';
import type { PropsWithChildren, ReactNode, RefObject } from 'react';
import { useState, useRef } from 'react';
import type { ViewStyle } from 'react-native';
import { KeyboardAvoidingView, Platform, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { createTheme as createDSTheme } from 'app/shopify';
import { Column } from '../box';
import { Text } from '../text';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import type { KeyboardAwareScrollViewProps } from 'react-native-keyboard-aware-scroll-view';
import { useBottomTabBarHeightOrDefault } from 'app/hooks/navigation';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import { useKeyboard } from '@react-native-community/hooks';
import { useUISize, isMobileNative } from 'app/utils/web-helpers';

const theme = createDSTheme();

export interface PageScrollViewRef {
  scrollTo?: (options: { x?: number; y?: number; animated?: boolean }) => void;
  scrollToPosition?: (x: number, y: number, animated?: boolean) => void;
}

interface PageProps extends KeyboardAwareScrollViewProps {
  accessory?: ReactNode;
  scrollRef?: RefObject<KeyboardAwareScrollView | ScrollView | null>;
  title?: string;
  stretchToFill?: boolean;
  isModal?: boolean;
  paddingStyle?: ViewStyle;
  pageScrollRef?: RefObject<PageScrollViewRef | null>;
}

export function usePagePadding(isModal?: boolean) {
  const insets = useSafeAreaInsets();
  const headerHeight = useHeaderHeight();
  const { isLargeUI } = useUISize();

  const defaultTopPadding = theme.spacing['spacing.general.x8'];

  const paddingTop = (() => {
    if (insets.top && headerHeight <= 0) {
      if (isModal) {
        return insets.top + defaultTopPadding;
      }
      return insets.top;
    }
    return defaultTopPadding;
  })();
  const paddingProps = {
    paddingBottom: insets.bottom || theme.spacing['spacing.general.x4'],
    paddingLeft: insets.left || theme.spacing['spacing.general.x6'],
    paddingRight: insets.right || theme.spacing['spacing.general.x6'],
    paddingTop: paddingTop,
  };

  if (isLargeUI) {
    paddingProps.paddingLeft = theme.spacing['spacing.general.x3'];
    paddingProps.paddingRight = theme.spacing['spacing.general.x3'];
  }

  return paddingProps;
}

function getPageKeyboardOffset({
  accessoryHeight,
  deviceHeight,
  headerHeight,
  pageHeight,
  tabBarVisible,
}: {
  accessoryHeight: number;
  deviceHeight: number;
  headerHeight: number;
  pageHeight: number;
  tabBarVisible: boolean;
}) {
  if (!accessoryHeight) return 0;

  let offset = accessoryHeight;
  if (Platform.OS === 'android') offset += theme.spacing['spacing.general.x4'];

  // iOS before 26 needs some extra offset. Not sure why...
  if (tabBarVisible && Platform.OS === 'ios' && parseInt(Platform.Version.split('.')[0] ?? '0') < 26) {
    offset += 26;
  }

  // A non-fullscreen iOS modal leaves a top gap that also needs to be reflected here.
  if (Platform.OS === 'ios' && !tabBarVisible) offset += deviceHeight - headerHeight - pageHeight;
  return offset;
}

function PageTitle({ align, title }: { align: 'left' | 'center'; title?: string }) {
  if (!title || isMobileNative) return null;
  return (
    <Text fontWeight="600" marginBottom="spacing.general.x8" textAlign={align} variant="headerXSmall">
      {title}
    </Text>
  );
}

interface PageContentProps {
  accessory?: ReactNode;
  accessoryContainer: RefObject<View | null>;
  assignScrollRefs: (ref: KeyboardAwareScrollView | ScrollView | null) => void;
  children?: ReactNode;
  contentContainerStyle: KeyboardAwareScrollViewProps['contentContainerStyle'];
  paddingProps: { paddingBottom: number; paddingLeft: number; paddingRight: number; paddingTop: number } | ViewStyle;
  rest: KeyboardAwareScrollViewProps;
  style: KeyboardAwareScrollViewProps['style'];
  title?: string;
}

function LargePageContent({
  accessory,
  accessoryContainer,
  assignScrollRefs,
  children,
  contentContainerStyle,
  paddingProps,
  rest,
  style,
  title,
}: PageContentProps) {
  return (
    <Column style={{ flex: 1, justifyContent: 'space-between' }}>
      <ScrollView
        {...rest}
        ref={assignScrollRefs}
        contentContainerStyle={contentContainerStyle}
        style={{ ...(style as ViewStyle), flex: 1 }}
      >
        <View style={{ ...paddingProps, flex: 1 }}>
          <PageTitle align="left" title={title} />
          {children}
        </View>
      </ScrollView>

      {accessory ? <Column innerRef={accessoryContainer}>{accessory}</Column> : null}
    </Column>
  );
}

function SmallPageContent({
  accessory,
  accessoryContainer,
  assignScrollRefs,
  children,
  contentContainerStyle,
  keyboardShown,
  offset,
  onAccessoryLayout,
  paddingProps,
  rest,
  scrollableFlex,
  setContentHeight,
  setPageHeight,
  style,
  tabBarVisible,
  title,
}: PageContentProps & {
  keyboardShown: boolean;
  offset: number;
  onAccessoryLayout: () => void;
  scrollableFlex: number;
  setContentHeight: (height: number) => void;
  setPageHeight: (height: number) => void;
  tabBarVisible: boolean;
}) {
  const hasAccessory = Boolean(accessory);
  return (
    <View
      onLayout={(event) => setPageHeight(event.nativeEvent.layout.height)}
      style={{ flex: 1, justifyContent: 'space-between' }}
    >
      <KeyboardAwareScrollView
        {...rest}
        ref={assignScrollRefs}
        contentContainerStyle={{ ...paddingProps, ...(contentContainerStyle as ViewStyle) }}
        extraScrollHeight={rest.extraScrollHeight ?? 100}
        onLayout={(event) => setContentHeight(event.nativeEvent.layout.height)}
        style={{
          ...(style as ViewStyle),
          flex: hasAccessory ? 1 : scrollableFlex,
          minHeight: hasAccessory ? 0 : undefined,
        }}
      >
        <PageTitle align="center" title={title} />
        {children}
        {Platform.OS === 'android' ? (
          <View style={{ height: paddingProps.paddingBottom, marginBottom: paddingProps.paddingTop }} />
        ) : null}
      </KeyboardAwareScrollView>

      {accessory ? (
        <KeyboardAvoidingView
          behavior={Platform.OS === 'android' ? (keyboardShown || tabBarVisible ? 'padding' : undefined) : 'position'}
          keyboardVerticalOffset={offset}
          style={{ flexShrink: 0 }}
        >
          <Column onLayout={onAccessoryLayout} innerRef={accessoryContainer} style={{ flexShrink: 0 }}>
            {accessory}
          </Column>
        </KeyboardAvoidingView>
      ) : null}
    </View>
  );
}

// Keep this as a normal compiler-managed component; historical custom comparison broke form validation.
export function Page({
  children,
  accessory,
  scrollRef,
  title,
  stretchToFill = false,
  isModal,
  style,
  paddingStyle,
  contentContainerStyle,
  pageScrollRef,
  ...rest
}: PropsWithChildren<PageProps>) {
  const headerHeight = useHeaderHeight();
  const tabBarHeight = useBottomTabBarHeightOrDefault();
  const [accessoryHeight, setAccessoryHeight] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const deviceHeight = useDeviceHeight();

  const defaultPaddingStyle = usePagePadding(isModal);
  const paddingProps = paddingStyle ?? defaultPaddingStyle;
  const assignScrollRefs = (ref: KeyboardAwareScrollView | ScrollView | null) => {
    if (pageScrollRef) {
      pageScrollRef.current = ref;
    }

    if (scrollRef) {
      scrollRef.current = ref;
    }
  };

  const { isLargeUI, isSmallUI } = useUISize();

  const keyboard = useKeyboard();

  const accessoryContainer = useRef<View>(null);
  const onAccessoryLayout = () => {
    if (!accessoryContainer.current) return;
    requestAnimationFrame(() => {
      // Use requestAnimationFrame to ensure layout is settled
      measureAccessory();
    });
  };

  const measureAccessory = () => {
    if (!accessoryContainer.current) return;
    accessoryContainer.current.measure((_x, _y, _width, height, _pageX, _pageY) => {
      setAccessoryHeight(height);
    });
  };

  const tabBarVisible = tabBarHeight > 0;
  const offset = getPageKeyboardOffset({ accessoryHeight, deviceHeight, headerHeight, pageHeight, tabBarVisible });

  // Detect when content needs to stretch to fill the screen, and set de Page's (Scrollable) flex prop to 1 when it does.
  // Using flex=1 all the time would prevent scrolling on small devices, and using flex=0 makes the Scrollable not take the full available height, which we want to do, when stretchToFill = true.
  // On web with small browser (isSmallUI but not native mobile), always fill the available height.
  const isWebMobileView = isSmallUI && !isMobileNative;
  const scrollableFlex = isWebMobileView || (stretchToFill && contentHeight + accessoryHeight < pageHeight) ? 1 : 0;

  if (isLargeUI) {
    return (
      <LargePageContent
        accessory={accessory}
        accessoryContainer={accessoryContainer}
        assignScrollRefs={assignScrollRefs}
        contentContainerStyle={contentContainerStyle}
        paddingProps={paddingProps}
        rest={rest}
        style={style}
        title={title}
      >
        {children}
      </LargePageContent>
    );
  }

  return (
    <SmallPageContent
      accessory={accessory}
      accessoryContainer={accessoryContainer}
      assignScrollRefs={assignScrollRefs}
      contentContainerStyle={contentContainerStyle}
      keyboardShown={keyboard.keyboardShown}
      offset={offset}
      onAccessoryLayout={onAccessoryLayout}
      paddingProps={paddingProps}
      rest={rest}
      scrollableFlex={scrollableFlex}
      setContentHeight={setContentHeight}
      setPageHeight={setPageHeight}
      style={style}
      tabBarVisible={tabBarVisible}
      title={title}
    >
      {children}
    </SmallPageContent>
  );
}
